import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">SAANJH</h1>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-4">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Welcome to SAANJH ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our platform at saanjh.cc.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-medium mb-3">2.1 Personal Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Register as a vendor (business name, email, password)</li>
              <li>Make a purchase (name, email address)</li>
              <li>Subscribe to our services (payment information processed securely by Razorpay)</li>
              <li>Contact us for support</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">2.2 Automatic Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              We automatically collect certain information when you visit our platform, including:
              IP address, browser type, device information, and usage data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide, operate, and maintain our platform</li>
              <li>Process your transactions and subscriptions</li>
              <li>Send you order confirmations and updates</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our services and develop new features</li>
              <li>Prevent fraud and enhance security</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Payment Information Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong className="text-foreground">All payment transactions are processed securely through Razorpay, 
              a PCI DSS compliant payment gateway.</strong> We do not store your credit card, debit card, UPI, or 
              net banking credentials on our servers. Razorpay handles all sensitive payment information in 
              compliance with industry standards.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              For more information about Razorpay's security measures, please visit: 
              <a href="https://razorpay.com/security/" target="_blank" rel="noopener noreferrer" 
                 className="text-primary hover:underline ml-1">
                https://razorpay.com/security/
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Service Providers:</strong> Razorpay (payment processing), 
                  email service providers</li>
              <li><strong className="text-foreground">Vendors:</strong> Order information shared with relevant 
                  vendor to fulfill your purchase</li>
              <li><strong className="text-foreground">Legal Requirements:</strong> When required by law or to 
                  protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined 
              in this Privacy Policy, unless a longer retention period is required by law. Transaction records 
              are maintained for 7 years as per Indian tax regulations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Your Privacy Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for data processing</li>
              <li>Export your data</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise these rights, please contact us at admin@saanjh.cc
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our platform and store certain 
              information. You can instruct your browser to refuse all cookies or to indicate when a cookie is 
              being sent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our platform is not intended for individuals under the age of 18. We do not knowingly collect 
              personal information from children under 18. If you are a parent and believe your child has 
              provided us with personal information, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your information may be transferred to and maintained on servers located outside of India. 
              We ensure appropriate safeguards are in place to protect your data in accordance with this 
              Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
              the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review 
              this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-secondary p-6 rounded-lg">
              <p className="font-medium mb-2">SAANJH</p>
              <p className="text-muted-foreground">Head Office, Sant Nagar, Civil Lines</p>
              <p className="text-muted-foreground">Ludhiana - 141001, Punjab, India</p>
              <p className="text-muted-foreground mt-3">Email: admin@saanjh.cc</p>
              <p className="text-muted-foreground">Phone: +91 78376 40500</p>
            </div>
          </section>

          <section className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-blue-900">Payment Security Notice</h3>
            <p className="text-sm text-blue-800 leading-relaxed">
              🔒 <strong>Your payment information is safe.</strong> SAANJH uses Razorpay, a RBI-approved and 
              PCI DSS Level 1 certified payment gateway. We never store your card details, UPI credentials, 
              or banking information on our servers. All transactions are encrypted and processed through 
              Razorpay's secure infrastructure.
            </p>
          </section>
        </div>
      </div>

      <footer className="border-t py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SAANJH. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;